import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { BaseServices } from '../../../kuba.services';
import {
  CategoryDepartmentMeanDto,
  CategoryHistoryDto,
  SurveyCategoryDto,
  SurveyInsightsDto
} from '../analysis/models';
import {
  scoreBarDataset,
  scoreLineDataset,
  scoreScaleOptions
} from '../analysis/chart-theme.util';
import { WorkEnvironmentAnalysisService } from '../analysis/services/work-environment-analysis.service';

/**
 * Per-category analysis of one survey.
 *
 * An overview panel shows a gauge for every category side by side; selecting a
 * category shows its score, its department breakdown, its history, and the
 * average of each question it contains.
 *
 * Ported from the AMIL `Statistic` page and its `SurveyStatistic` component.
 * The prototype fetched every category gauge independently, one request per
 * gauge; the means are batched into a single `forkJoin` here, and the heavier
 * per-category charts load only for the category being looked at.
 */
@Component({
  selector: 'survey-analysis',
  templateUrl: './survey-analysis.component.html',
  styleUrls: ['./survey-analysis.component.scss'],
  standalone: false
})
export class SurveyAnalysisComponent implements OnChanges, OnDestroy {
  /** The survey to analyse. Null while the business has no surveys. */
  @Input() surveyId: number | null = null;

  //#region Variables
  private readonly businessId = BaseServices.BusinessId;

  /** Null selects the all-categories overview panel. */
  activeCategoryId: number | null = null;

  categories: SurveyCategoryDto[] = [];
  categoryMeans: Record<number, number> = {};
  insights: SurveyInsightsDto | null = null;

  isLoading = false;
  isCategoryLoading = false;

  categoryDepartmentChartData: any = null;
  categoryHistoryChartData: any = null;
  chartOptions: any = null;

  private subscriptions: Subscription[] = [];
  private categorySubscription: Subscription | null = null;
  //#endregion

  constructor(
    private analysisService: WorkEnvironmentAnalysisService,
    private translate: TranslateService
  ) {
    this.chartOptions = scoreScaleOptions(5);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('surveyId' in changes) {
      this.activeCategoryId = null;
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.categorySubscription?.unsubscribe();
  }

  //#region Tabs
  selectOverview(): void {
    this.activeCategoryId = null;
  }

  selectCategory(categoryId: number): void {
    if (this.activeCategoryId === categoryId) {
      return;
    }

    this.activeCategoryId = categoryId;
    this.loadCategory(categoryId);
  }

  get activeCategory(): SurveyCategoryDto | null {
    return (
      this.categories.find(category => category.Id === this.activeCategoryId) ??
      null
    );
  }

  meanFor(categoryId: number): number | null {
    const mean = this.categoryMeans[categoryId];
    return mean === undefined ? null : mean;
  }
  //#endregion

  //#region Data
  private load(): void {
    if (!this.surveyId) {
      this.categories = [];
      return;
    }

    const surveyId = this.surveyId;
    this.isLoading = true;

    this.subscriptions.push(
      this.analysisService.getCategories(surveyId).subscribe({
        next: categories => {
          this.categories = categories;
          this.loadCategoryMeans(surveyId, categories);
        },
        error: () => {
          this.categories = [];
          this.isLoading = false;
        }
      })
    );

    this.subscriptions.push(
      this.analysisService.getInsights(surveyId).subscribe({
        next: insights => (this.insights = insights),
        error: () => (this.insights = null)
      })
    );
  }

  /** One batched request set for the gauges on the overview panel. */
  private loadCategoryMeans(
    surveyId: number,
    categories: SurveyCategoryDto[]
  ): void {
    if (!categories.length) {
      this.isLoading = false;
      return;
    }

    this.subscriptions.push(
      forkJoin(
        categories.map(category =>
          this.analysisService.getCategoryMean(surveyId, category.Id)
        )
      ).subscribe({
        next: means => {
          this.categoryMeans = {};
          means.forEach(mean => {
            this.categoryMeans[mean.CategoryId] = mean.MeanValue;
          });
          this.isLoading = false;
        },
        error: () => {
          this.categoryMeans = {};
          this.isLoading = false;
        }
      })
    );
  }

  /** Department breakdown and history for the category being looked at. */
  private loadCategory(categoryId: number): void {
    if (!this.surveyId) {
      return;
    }

    // Switching category mid-request would otherwise let a slow response for
    // the previous category paint over the new one.
    this.categorySubscription?.unsubscribe();

    this.isCategoryLoading = true;
    this.categoryDepartmentChartData = null;
    this.categoryHistoryChartData = null;

    this.categorySubscription = forkJoin({
      departments: this.analysisService.getCategoryDepartmentMeans(
        this.surveyId,
        categoryId
      ),
      history: this.analysisService.getCategoryHistory(
        this.businessId,
        categoryId
      )
    }).subscribe({
      next: result => {
        this.buildCategoryDepartmentChart(result.departments);
        this.buildCategoryHistoryChart(result.history);
        this.isCategoryLoading = false;
      },
      error: () => {
        this.isCategoryLoading = false;
      }
    });
  }
  //#endregion

  //#region Charts
  private buildCategoryDepartmentChart(
    departments: CategoryDepartmentMeanDto[]
  ): void {
    if (!departments.length) {
      this.categoryDepartmentChartData = null;
      return;
    }

    this.categoryDepartmentChartData = {
      labels: departments.map(department => department.DepartmentName),
      datasets: [
        scoreBarDataset(
          this.translate.instant('WORK_ENVIRONMENT_SURVEY.AVERAGE_SCORE'),
          departments.map(department => department.MeanValue)
        )
      ]
    };
  }

  private buildCategoryHistoryChart(history: CategoryHistoryDto): void {
    if (!history.Points.length) {
      this.categoryHistoryChartData = null;
      return;
    }

    this.categoryHistoryChartData = {
      labels: history.Points.map(point => point.DateForScore),
      datasets: [
        scoreLineDataset(
          this.translate.instant('WORK_ENVIRONMENT_SURVEY.AVERAGE_SCORE'),
          history.Points.map(point => point.AverageScore)
        )
      ]
    };
  }
  //#endregion
}
